import { createFileRoute, useRouter } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { readFinalScore } from '~/lib/finalScore';
import type { PublicLandingPage } from '~/types';

const SCORE_DISPLAY_MS = 5000;

export const Route = createFileRoute('/$slug/score')({
  component: ScorePage,
});

function ScorePage() {
  const router = useRouter();
  const { slug, landingPage } = Route.useRouteContext() as {
    slug: string;
    landingPage: PublicLandingPage;
  };
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  // Read the server-finalized totals stashed by GamePlayer at finalize-time.
  // When absent (deep link, refresh, or finalize failure with no fallback),
  // we render a zero-state — never reach for URL search params.
  const finalScore = useMemo(() => readFinalScore(slug), [slug]);

  const score = finalScore?.score ?? 0;
  const correctAnswers = finalScore?.correctAnswers ?? 0;
  const totalQuestions = finalScore?.totalQuestions ?? 0;
  const pointsPerCorrectAnswer =
    finalScore?.pointsPerCorrectAnswer ?? landingPage.gameOptions.pointsPerCorrectAnswer;
  const maxPoints = totalQuestions * pointsPerCorrectAnswer;
  const percentage = maxPoints > 0 ? Math.round((score / maxPoints) * 100) : 0;
  // When the server finalize call failed, the displayed score is client-derived
  // and the server has no record. Surface that qualifier non-blockingly — the
  // claim-handoff flow (offer page) still works.
  const hadFinalizeError = finalScore?.hadFinalizeError === true;

  const handleComplete = useCallback(() => {
    router.navigate({ to: '/$slug/offer', params: { slug } });
  }, [router, slug]);

  // Auto-transition to offer after delay
  useEffect(() => {
    timerRef.current = setTimeout(handleComplete, SCORE_DISPLAY_MS);
    return () => clearTimeout(timerRef.current);
  }, [handleComplete]);

  return (
    <div
      className="h-[100dvh] w-screen flex items-center justify-center p-0 lg:p-8 xl:p-16"
      style={{
        backgroundImage: 'url(https://s3.amazonaws.com/yak.assets/52xP82c.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <div
        aria-live="polite"
        className="sr-only"
      >
        Your score
      </div>
      <div className="w-full h-full lg:h-auto lg:max-w-7xl lg:aspect-video lg:rounded-2xl overflow-hidden bg-white/95 lg:shadow-2xl flex flex-col items-center justify-center p-4 lg:p-12">
        {hadFinalizeError && (
          <div
            role="status"
            data-testid="finalize-error-banner"
            className="mb-3 md:mb-6 px-3 py-1.5 md:px-4 md:py-2 rounded-md bg-amber-50 border border-amber-300 text-amber-900 text-xs md:text-sm font-medium"
          >
            Score not confirmed — please try again next time.
          </div>
        )}
        <div className="flex flex-col items-center gap-4 md:gap-8 text-center animate-in fade-in zoom-in-95 duration-700">
          {landingPage.branding.logoUrl && (
            <img
              src={landingPage.branding.logoUrl}
              alt="Brand logo"
              className="h-12 md:h-20 object-contain"
            />
          )}
          <div className="flex flex-col gap-2 md:gap-4">
            <p className="text-lg md:text-2xl font-semibold text-gray-700">
              You got {correctAnswers} of {totalQuestions} correct
            </p>
            <h1
              className="text-4xl md:text-6xl font-bold"
              style={{ color: landingPage.branding.primaryColor }}
            >
              {score}/{maxPoints}
            </h1>
            <p className="text-sm md:text-lg font-medium text-gray-500">
              points earned out of {maxPoints} possible
            </p>
            <p
              className="text-2xl md:text-4xl font-semibold"
              style={{ color: landingPage.branding.secondaryColor }}
            >
              {percentage}%
            </p>
          </div>
          <p className="text-lg md:text-2xl font-medium text-gray-700">
            Thanks for playing!
          </p>
        </div>
      </div>
    </div>
  );
}
